import { plainToInstance, Type } from 'class-transformer'
import { Entity } from '@core/entities/entity'
import { ITemplateProjectLocations } from './ITemplateProjectLocations'
import { PinResponse } from '@core/entities/skia/PinResponse'
import { localizedStrings } from '@core/strings'
import { Pin } from '@core/entities/skia/Pin'

export class TemplateProjectLocations extends Entity<ITemplateProjectLocations> {
  locations: Array<{
    locationNodeId: string
    locationName: string
    // Will be talking with backend to change the schema here
    locationPlanId?: number
  }>
  areas?: Array<{
    locationPlanId?: number
    locationNode?: { id: string }
    locationPlan?: { id: number }
    selectedAreas?: Array<{
      uuid: string
      name?: string
      color?: string
    }>
  }>

  @Type(() => PinResponse)
  pins?: Array<PinResponse>

  public static new(payload: unknown): TemplateProjectLocations {
    const entity = plainToInstance(TemplateProjectLocations, payload)

    return entity
  }

  public getSelectedPermits(): string[] {
    // Return an array of unique area names
    return this.areas
      .flatMap((area) =>
        area.selectedAreas.map((selectedArea) => selectedArea.name),
      )
      .reduce(
        (uniqueNames, name) =>
          uniqueNames.includes(name) ? uniqueNames : [...uniqueNames, name],
        [],
      )
  }

  public getPins(): Pin[] {
    const pins = this.pins?.flatMap((pin) => pin.droppedPins)

    return pins
  }

  public getHumanReadableValueString(): string {
    const selectedZoneIds = this.areas?.flatMap((area) =>
      area.selectedAreas?.map((selectedArea) => selectedArea.uuid),
    )
    const pins = this.pins?.flatMap((pin) => pin.droppedPins)

    // Returns a label for the number of pins in a zone
    const getPinLabel = (numberOfPins: number) =>
      numberOfPins > 0
        ? ` (${numberOfPins} ${localizedStrings.pin}${
            numberOfPins > 1 ? 's' : ''
          })`
        : ''

    // This is the label for the pins not in a zone
    const getIndividualPins = () => {
      const individualPins = pins?.filter(
        (pin) =>
          !selectedZoneIds?.find((zone) => pin.areaUuids?.includes(zone)),
      )

      const pinLabel = getPinLabel(individualPins?.length)

      return pinLabel ? `, ${localizedStrings.noZone}${pinLabel}` : ''
    }

    // This is the label for selected zones and pins
    const getSelectedZonesAndPinsLabel = () => {
      return selectedZoneIds?.map((zone) => {
        const pinsInZone = pins?.filter((pin) => pin.areaUuids?.includes(zone))

        const zoneName = this.areas
          ?.find((area) =>
            area.selectedAreas?.find(
              (selectedArea) => selectedArea.uuid === zone,
            ),
          )
          ?.selectedAreas?.find(
            (selectedArea) => selectedArea.uuid === zone,
          )?.name
        return `${zoneName}${getPinLabel(pinsInZone.length)}`
      })
    }

    // This is the complete label for all cases
    const getZonePinsLabel = () => {
      let label: string | string[] = localizedStrings.selectZone

      const selectedZonesAndPinsLabel = getSelectedZonesAndPinsLabel()

      if (selectedZonesAndPinsLabel?.length) {
        label = selectedZonesAndPinsLabel.join(', ')
      }

      const individualPinsLabel = getIndividualPins()

      if (individualPinsLabel?.length) {
        label += individualPinsLabel
      }

      return label as string
    }

    return getZonePinsLabel()
  }
}
